import React from 'react';
import { useTranslation } from 'react-i18next';
import { humanize } from 'underscore.string';
import { FilterDropdown } from '../molecules/FilterDropdown';
import { FilterSingleSelect } from '../molecules/FilterSingleSelect';
import { FilterToggle } from '../molecules/FilterToggle';
// Re-export shared organisms so existing imports still work
export { ReferenceFilter } from './ReferenceFilter';
export { MarketRegionFilter } from './MarketRegionFilter';
export { OpsGroupFilter } from './OpsGroupFilter';
export { DateRangeFilter as AccessorialDateRangeFilter, DateRangeWithTypeFilter, } from './DateRangeFilter';
// ---------------------------------------------------------------------------
// Domain constants
// ---------------------------------------------------------------------------
const STATUS_OPTIONS = [
    { localizationSlug: 'PENDING_ADMIN', value: 'pending-admin' },
    { localizationSlug: 'PENDING_SHIPPER', value: 'pending-shipper' },
    { localizationSlug: 'ACCEPTED', value: 'accepted' },
    { localizationSlug: 'APPROVED', value: 'approved' },
    { localizationSlug: 'DECLINED', value: 'declined' },
];
const ACTION_TYPE_OPTIONS = [
    { localizationSlug: 'AWAITING_ADMIN_REVIEW', value: 'Awaiting Admin Review' },
    {
        localizationSlug: 'AWAITING_SHIPPER_RESPONSE',
        value: 'Awaiting Shipper Response',
    },
];
const PAYEE_TYPE_OPTIONS = ['carrier', 'vendor', 'partner'];
// ---------------------------------------------------------------------------
// Helper: adapt value/onChange to FilterDropdown's onCheckedChange
// ---------------------------------------------------------------------------
function useDropdownAdapter(value, onChange, allValues) {
    return {
        selected: value,
        onCheckedChange: (item, checked) => {
            onChange(checked ? [...value, item] : value.filter((v) => v !== item));
        },
        onSelectAll: () => onChange([...allValues]),
        onClear: () => onChange([]),
    };
}
export function AccessorialStatusFilter({ value, onChange, isShipper, }) {
    const { t } = useTranslation();
    const options = STATUS_OPTIONS.map((s) => ({
        value: s.value,
        label: humanize(t(s.localizationSlug)),
    }));
    const adapter = useDropdownAdapter(value, onChange, STATUS_OPTIONS.map((s) => s.value));
    return (React.createElement(FilterDropdown, Object.assign({ title: isShipper ? t('SHIPPER_STATUS') : t('CARRIER_STATUS'), options: options }, adapter)));
}
export function ActionTypeFilter({ value, onChange }) {
    const { t } = useTranslation();
    const options = ACTION_TYPE_OPTIONS.map((type) => ({
        value: type.value,
        label: t(type.localizationSlug),
    }));
    const adapter = useDropdownAdapter(value, onChange, ACTION_TYPE_OPTIONS.map((a) => a.value));
    return (React.createElement(FilterDropdown, Object.assign({ title: t('ACTION_TYPES'), options: options }, adapter)));
}
export function PayeeTypeFilter({ value, onChange }) {
    const { t } = useTranslation();
    const options = PAYEE_TYPE_OPTIONS.map((type) => ({
        value: type,
        label: humanize(t(type.toUpperCase(), { count: 1 })),
    }));
    const adapter = useDropdownAdapter(value, onChange, [...PAYEE_TYPE_OPTIONS]);
    return (React.createElement(FilterDropdown, Object.assign({ title: t('PAYEE_TYPES'), options: options }, adapter)));
}
export function MissingDocumentsFilter({ value, onChange }) {
    const { t } = useTranslation();
    const adapter = useDropdownAdapter(value ? ['true'] : [], (newValue) => onChange(newValue.includes('true')), ['true']);
    return (React.createElement(FilterDropdown, Object.assign({ title: t('MISSING_DOCS_VALUES'), options: [{ value: 'true', label: t('MISSING') }] }, adapter)));
}
export function NeedsReviewFilter({ value, onChange }) {
    const { t } = useTranslation();
    const options = [
        { value: 'true', label: t('NEEDS_REVIEW_TRUE', { count: 1 }) },
        { value: 'false', label: t('NEEDS_REVIEW_FALSE', { count: 1 }) },
    ];
    const adapter = useDropdownAdapter(value.map(String), (newValue) => onChange(newValue), ['true', 'false']);
    return (React.createElement(FilterDropdown, Object.assign({ title: t('NEEDS_REVIEW'), options: options }, adapter)));
}
export function AccessorialTypeFilter({ value, onChange, accessorials, }) {
    const { t } = useTranslation();
    const options = accessorials.map((type) => ({
        value: type.slug,
        label: type.name,
    }));
    const adapter = useDropdownAdapter(value, onChange, accessorials.map((a) => a.slug));
    return (React.createElement(FilterDropdown, Object.assign({ title: t('SELECT_ACCESSORIAL_TYPES'), options: options }, adapter)));
}
export function ReasonCodeFilter({ value, onChange, reasonCodes, }) {
    const { t } = useTranslation();
    const options = reasonCodes.map((rc) => ({ value: rc, label: rc }));
    return (React.createElement(FilterSingleSelect, { title: t('SELECT'), options: options, value: value, onChange: onChange }));
}
export function QuickpayCarrierFilter({ value, onChange }) {
    return React.createElement(FilterToggle, { checked: value, onCheckedChange: onChange });
}
